import type { CounterpartNotice, CounterpartSwitches, SilentCounterpart, SwitchMode } from './api.ts';
import { formatWhen } from './format.ts';

/**
 * Ampel des Systemstatus in der Kopfzeile (Story 1.98, EXPERIENCE §2.4 „Systemstatus“). Nur aus den Daten, die die
 * frühere Startseiten-Karte zeigte: Betriebsschalter je Gegenstelle und „Wichtige Hinweise“ (nicht erreichbare
 * Gegenstellen). Reine Funktion, keine eigene Fachregel.
 */
export type SystemStatusLevel = 'ok' | 'warn' | 'error';

export const SYSTEM_STATUS_TEXTS = {
  title: 'Systemstatus',
  loading: 'Systemstatus wird geladen',
  ok: 'Alle Schnittstellen laufen normal',
  failed: 'Störung: Systemstatus lässt sich nicht abrufen',
  switchesLink: 'Zu den Betriebsschaltern',
  switchesHeading: 'Stellung der Betriebsschalter',
  disturbances: 'Störungen',
  /** Zeile unter einer Störung (vorher Karte „Wichtige Hinweise“ der Startseite, seit Story 1.115 nur hier). */
  noticeMeta: 'Vorgänge laufen automatisch nach',
  waiting: (n: number) => (n === 1 ? '1 Vorgang wartet' : `${n} Vorgänge warten`),
  modes: { off: 'aus', shadow: 'Probebetrieb', live: 'live' } satisfies Record<SwitchMode, string>,
  read: 'lesen',
  write: 'senden',
  close: 'Schließen',
} as const;

export interface SystemStatus {
  level: SystemStatusLevel;
  /** Ein Satz für Tooltip und Screenreader. */
  sentence: string;
}

/**
 * Worte je Gegenstelle, nur für vorhandene Richtungen und immer mit Beschriftung: „lesen live · senden Probebetrieb“
 * (eine Gegenstelle kann nur „lesen“ oder nur „senden“ haben). Punkt: live nur, wenn alles live ist; sonst Probebetrieb,
 * wenn eine Richtung im Probebetrieb steht; sonst aus.
 */
export function switchWords(c: CounterpartSwitches): { main: SwitchMode; words: string } {
  const t = SYSTEM_STATUS_TEXTS;
  const present = (['read', 'write'] as const)
    .map((direction) => ({ direction, mode: c.switches.find((s) => s.direction === direction)?.mode }))
    .filter((s): s is { direction: 'read' | 'write'; mode: SwitchMode } => s.mode !== undefined);
  const modes = present.map((s) => s.mode);
  return {
    main: modes.every((m) => m === 'live') ? 'live' : modes.includes('shadow') ? 'shadow' : 'off',
    words: present.map((s) => `${t[s.direction]} ${t.modes[s.mode]}`).join(' · '),
  };
}

/**
 * Stille Gegenstellen ohne die, die schon als „nicht erreichbar“ gemeldet sind (Kennung der Gegenstelle): Eine nicht
 * erreichbare Gegenstelle meldet sich zwangsläufig nicht – eine Störung, ein Eintrag (Kopfzeile, Liste).
 */
export function silentOnly<T extends Pick<SilentCounterpart, 'counterpart'>>(notices: readonly Pick<CounterpartNotice, 'counterpart'>[], silent: readonly T[]): T[] {
  const unreachable = new Set(notices.map((n) => n.counterpart));
  return silent.filter((s) => !unreachable.has(s.counterpart));
}

/**
 * Rot: mindestens ein Wichtiger Hinweis (Gegenstelle nicht erreichbar), eine stille Gegenstelle (länger ohne Meldung
 * als ihr Mindest-Meldetakt, Story 1.147) oder Abruf fehlgeschlagen. Gezählt wird je Gegenstelle (Kennung), nicht je
 * Hinweis. Gelb: sonst mindestens ein Schalter (lesen oder senden) nicht „live“. Grün: alle live, keine Störung.
 */
export function systemStatus(input: {
  counterparts: CounterpartSwitches[];
  notices: Pick<CounterpartNotice, 'counterpart' | 'label'>[];
  silent?: Pick<SilentCounterpart, 'counterpart' | 'text'>[];
  failed?: boolean;
}): SystemStatus {
  const notices = [...new Map(input.notices.map((n) => [n.counterpart, n] as const)).values()];
  const silent = silentOnly(notices, [...new Map((input.silent ?? []).map((s) => [s.counterpart, s] as const)).values()]);
  if (notices.length > 1 && silent.length === 0) return { level: 'error', sentence: `Störung: ${notices.length} Schnittstellen sind nicht erreichbar` };
  if (silent.length > 1 && notices.length === 0) return { level: 'error', sentence: `Störung: ${silent.length} Schnittstellen melden sich nicht` };
  if (notices.length + silent.length > 1) {
    return { level: 'error', sentence: `Störung: ${notices.length + silent.length} Schnittstellen sind nicht erreichbar oder melden sich nicht` };
  }
  if (notices.length === 1) return { level: 'error', sentence: `Störung: ${notices[0]!.label} ist nicht erreichbar` };
  if (silent.length === 1) return { level: 'error', sentence: `Störung: ${silent[0]!.text}` };
  if (input.failed) return { level: 'error', sentence: SYSTEM_STATUS_TEXTS.failed };
  // Nur vorhandene Schalter zählen (eine Gegenstelle kann nur „lesen“ oder nur „senden“ haben).
  const notLive = input.counterparts.filter((c) => c.switches.some((s) => s.mode !== 'live'));
  if (notLive.length === 1) return { level: 'warn', sentence: `${notLive[0]!.label} nicht live (aus oder Probebetrieb)` };
  if (notLive.length > 1) return { level: 'warn', sentence: `${notLive.length} Schnittstellen nicht live (aus oder Probebetrieb)` };
  return { level: 'ok', sentence: SYSTEM_STATUS_TEXTS.ok };
}

/**
 * Titel einer Störung im Systemstatus: „Verbindungstest nicht erreichbar seit 10:02“ (an einem früheren Tag mit Datum).
 * Seit Story 1.115 ([RUDOLF 13.10.2026]) nur noch in der Kopfzeile; die Startseiten-Karte „Wichtige Hinweise“ entfiel.
 */
export function noticeTitle(notice: Pick<CounterpartNotice, 'label' | 'since'>, now: Date = new Date()): string {
  return `${notice.label} nicht erreichbar seit ${formatWhen(notice.since, now)}`;
}

/** Zweite Zeile: „Vorgänge laufen automatisch nach · 5 Vorgänge warten“ (ohne wartende nur der erste Teil). */
export function noticeMeta(notice: Pick<CounterpartNotice, 'waiting'>): string {
  return notice.waiting > 0 ? `${SYSTEM_STATUS_TEXTS.noticeMeta} · ${SYSTEM_STATUS_TEXTS.waiting(notice.waiting)}` : SYSTEM_STATUS_TEXTS.noticeMeta;
}
